import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Calendar, ChevronLeft, ChevronRight, Loader2, Clock, FileText
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { GENERAL_STATUS } from '@/lib/attendanceTypes';
import { formatLocalDateString, parseLocalDate } from '@/lib/dateUtils';

// Leave type colors (matching MyCalendarPage)
const getLeaveTypeColor = (type) => {
  const colors = {
    'annual_leave': 'bg-blue-100 text-blue-800 border-blue-200',
    'sick_leave': 'bg-red-100 text-red-800 border-red-200',
    'unpaid_leave': 'bg-gray-100 text-gray-800 border-gray-200',
    'work_from_home': 'bg-purple-100 text-purple-800 border-purple-200',
    'compassionate_leave': 'bg-orange-100 text-orange-800 border-orange-200',
    'parental_leave': 'bg-pink-100 text-pink-800 border-pink-200',
    'training': 'bg-green-100 text-green-800 border-green-200'
  };
  return colors[type] || colors['annual_leave'];
};

const getLeaveTypeAbbr = (type) => {
  const abbr = {
    'annual_leave': 'AL',
    'sick_leave': 'SL',
    'unpaid_leave': 'UL',
    'work_from_home': 'WFH',
    'compassionate_leave': 'CL',
    'parental_leave': 'PL',
    'training': 'TR'
  };
  return abbr[type] || 'L';
};

const getLeaveTypeName = (type) => {
  const names = {
    'annual_leave': 'Annual Leave',
    'sick_leave': 'Sick Leave',
    'unpaid_leave': 'Unpaid Leave',
    'work_from_home': 'Work From Home',
    'compassionate_leave': 'Compassionate Leave',
    'parental_leave': 'Parental Leave',
    'training': 'Training'
  };
  return names[type] || 'Leave';
};

const formatDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (start.toDateString() === end.toDateString()) {
    return start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  return `${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
};

export function TeamCalendarTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [requests, setRequests] = useState([]);
  const [bankHolidays, setBankHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadCalendarData = async () => {
      try {
        setLoading(true);

        // Get date range for current month
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

        // Get all company user IDs
        const { data: companyUsers } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('company_id', company.id);

        const companyUserIds = (companyUsers || []).map(u => u.id);

        if (companyUserIds.length === 0) {
          setRequests([]);
          setBankHolidays([]);
          setLoading(false);
          return;
        }

        // Load leave requests
        const { data: requestsData, error: requestsError } = await supabase
          .from('leave_requests')
          .select(`
            *,
            user_profiles!leave_requests_user_id_fkey(
              first_name,
              last_name
            )
          `)
          .in('user_id', companyUserIds)
          .eq('status', 'approved')
          .lte('start_date', endOfMonth.toISOString().split('T')[0])
          .gte('end_date', startOfMonth.toISOString().split('T')[0]);

        if (requestsError) throw requestsError;

        // Load bank holidays for current year
        const { data: holidaysData } = await supabase
          .from('bank_holidays')
          .select('*')
          .eq('status', GENERAL_STATUS.ACTIVE)
          .gte('date', `${currentDate.getFullYear()}-01-01`)
          .lte('date', `${currentDate.getFullYear()}-12-31`);

        setRequests(requestsData || []);
        setBankHolidays(holidaysData || []);
      } catch (error) {
        console.error('Failed to load calendar:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCalendarData();
  }, [company?.id, currentDate]);

  // Generate calendar days (Sunday start to match MyCalendarPage)
  const days = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const result = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startDayOfWeek; i++) {
      result.push(null);
    }

    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      result.push(new Date(year, month, day));
    }

    return result;
  }, [currentDate]);

  const getLeaveForDate = (date) => {
    if (!date) return [];
    const dateStr = formatLocalDateString(date);
    return requests.filter(request => {
      const startDate = parseLocalDate(request.start_date);
      const endDate = parseLocalDate(request.end_date);
      const checkDate = parseLocalDate(dateStr);
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  const isBankHoliday = (date) => {
    if (!date) return false;
    const dateStr = formatLocalDateString(date);
    return bankHolidays.some(h => h.date === dateStr);
  };

  const getBankHolidayName = (date) => {
    if (!date) return null;
    const dateStr = formatLocalDateString(date);
    const holiday = bankHolidays.find(h => h.date === dateStr);
    return holiday?.name;
  };

  const isWeekend = (date) => {
    if (!date) return false;
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const goToFullCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    navigate(`/attendance/my-calendar?year=${year}&month=${month}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
      </div>
    );
  }

  const monthName = currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  return (
    <TooltipProvider delayDuration={200}>
      <div className="h-full flex flex-col">
        {/* Header with month nav - matching MyCalendarPage style */}
        <div className="flex items-center justify-between mb-2 pb-2 border-b">
          <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={previousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{monthName}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
              className="h-6 px-2 text-xs font-semibold text-violet-600 border-violet-200 hover:bg-violet-50"
            >
              Today
            </Button>
          </div>
          <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={nextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-[10px] font-semibold text-gray-600 py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid - matching MyCalendarPage style */}
        <div className="flex-1 grid grid-cols-7 gap-1 overflow-hidden">
          {days.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="min-h-[50px]" />;
            }

            const leaveRequests = getLeaveForDate(date);
            const isBH = isBankHoliday(date);
            const holidayName = getBankHolidayName(date);
            const weekend = isWeekend(date);
            const today = isToday(date);
            const hasEvents = isBH || leaveRequests.length > 0;

            const MAX_DISPLAY = 2;
            const displayedRequests = leaveRequests.slice(0, MAX_DISPLAY);
            const remainingCount = leaveRequests.length - MAX_DISPLAY;

            const calendarCell = (
              <div
                onClick={goToFullCalendar}
                className={cn(
                  "min-h-[50px] border rounded p-1 transition-all hover:shadow-md cursor-pointer overflow-hidden",
                  today && "ring-2 ring-violet-600 bg-violet-50",
                  weekend && !today && "bg-gray-50",
                  isBH && !today && "bg-yellow-50",
                  leaveRequests.length > 0 && !today && !isBH && "bg-blue-50"
                )}
              >
                <div className="flex flex-col h-full">
                  {/* Date Number */}
                  <div className={cn(
                    "text-[10px] font-medium mb-0.5",
                    today && "text-violet-600",
                    weekend && !today && "text-gray-500",
                    !weekend && !today && "text-gray-900"
                  )}>
                    {date.getDate()}
                  </div>

                  {/* Bank Holiday */}
                  {isBH && (
                    <div className="mb-0.5">
                      <Badge className="text-[8px] px-1 py-0 bg-yellow-100 text-yellow-800 border-yellow-200">
                        BH
                      </Badge>
                    </div>
                  )}

                  {/* Leave Requests */}
                  <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                    {displayedRequests.map((request, idx) => (
                      <div
                        key={`${request.id}-${idx}`}
                        className={cn(
                          "text-[8px] px-1 py-0 border rounded truncate font-medium",
                          getLeaveTypeColor(request.leave_type)
                        )}
                        title={`${request.user_profiles?.first_name} ${request.user_profiles?.last_name}`}
                      >
                        {request.user_profiles?.first_name} {request.user_profiles?.last_name?.[0]}.
                      </div>
                    ))}
                    {remainingCount > 0 && (
                      <div className="text-[8px] px-1 py-0 bg-gray-200 text-gray-700 rounded font-medium">
                        +{remainingCount} more
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );

            // Only wrap in tooltip if there are events
            if (!hasEvents) {
              return <div key={date.toISOString()}>{calendarCell}</div>;
            }

            return (
              <Tooltip key={date.toISOString()}>
                <TooltipTrigger asChild>
                  {calendarCell}
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs p-0 max-h-[300px] overflow-y-auto bg-white">
                  <div className="space-y-2 p-3">
                    {/* Date header */}
                    <div className="font-semibold text-sm text-gray-900 border-b pb-1">
                      {date.toLocaleDateString('en-GB', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>

                    {/* Bank Holiday */}
                    {isBH && (
                      <div className="flex items-start gap-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                        <Calendar className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-medium text-sm text-yellow-900">Bank Holiday</div>
                          <div className="text-xs text-yellow-700">{holidayName}</div>
                        </div>
                      </div>
                    )}

                    {/* Leave Requests */}
                    {leaveRequests.map((request, idx) => (
                      <div key={`${request.id}-${idx}`} className="flex items-start gap-2 p-2 bg-gray-50 rounded border">
                        <div className="flex-shrink-0 mt-0.5">
                          <Badge className={cn("text-xs", getLeaveTypeColor(request.leave_type))}>
                            {getLeaveTypeAbbr(request.leave_type)}
                          </Badge>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900">
                            {getLeaveTypeName(request.leave_type)}
                          </div>
                          <div className="text-xs text-gray-900 font-medium mt-0.5">
                            {request.user_profiles?.first_name} {request.user_profiles?.last_name}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-600 mt-0.5">
                            <Clock className="h-3 w-3" />
                            {formatDateRange(request.start_date, request.end_date)}
                          </div>
                          {request.user_note && (
                            <div className="flex items-start gap-1 text-xs text-gray-600 mt-1">
                              <FileText className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-2">{request.user_note}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/* Footer - link to full calendar */}
        <button
          onClick={goToFullCalendar}
          className="mt-2 pt-2 border-t flex items-center justify-center gap-1 text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors"
        >
          <Calendar className="h-3.5 w-3.5" />
          <span>Open Full Calendar</span>
        </button>
      </div>
    </TooltipProvider>
  );
}
